import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  MessageCircle,
  Upload,
  User,
  LogOut,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const StaffWorkspace = () => {
  const { user, logout, API } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State management
  const [myRequests, setMyRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    assigned: 0,
    completed: 0,
    pending: 0
  });

  // Load staff data
  const loadStaffData = async () => {
    try {
      setLoading(true);
      
      // Get all requests for staff member
      const requestsResponse = await axios.get(`${API}/requests`);
      const allRequests = requestsResponse.data || [];
      
      // Filter requests assigned to this staff member
      const assignedRequests = allRequests.filter(req => 
        req.assigned_staff_id === user?.id || req.status === 'assigned'
      );
      
      setMyRequests(assignedRequests);
      
      // Calculate stats
      const assigned = assignedRequests.length;
      const completed = assignedRequests.filter(req => req.status === 'completed').length;
      const pending = assignedRequests.filter(req => req.status === 'pending' || req.status === 'assigned').length;
      
      setStats({ assigned, completed, pending });
      
      toast.success(`Loaded ${assigned} assigned requests`);
      
    } catch (error) {
      console.error('Error loading staff data:', error);
      toast.error('Failed to load staff requests');
    } finally {
      setLoading(false);
    }
  };

  // Update request status
  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      console.log('Updating request status:', { requestId, newStatus });
      console.log('API URL:', `${API}/requests/${requestId}/status`);
      
      const response = await axios.put(`${API}/requests/${requestId}/status`, {
        status: newStatus
      });
      
      console.log('Status update response:', response.data);
      toast.success(`Request status updated to ${newStatus}`);
      
      // Reload data
      await loadStaffData();
      if (selectedRequest && selectedRequest.id === requestId) {
        const updatedRequest = myRequests.find(req => req.id === requestId);
        if (updatedRequest) {
          setSelectedRequest({ ...updatedRequest, status: newStatus });
        }
      }
      
    } catch (error) {
      console.error('Status update error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to update request status: ${errorMessage}`);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRequest) return;
    
    try {
      await axios.post(`${API}/requests/${selectedRequest.id}/messages`, {
        content: newMessage
      });
      
      setNewMessage('');
      toast.success('Message sent');
      
      // Reload messages
      await loadMessages(selectedRequest.id);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Load messages for a request
  const loadMessages = async (requestId) => {
    try {
      const response = await axios.get(`${API}/requests/${requestId}/messages`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  };

  // Clean description by removing template text
  const cleanDescription = (description) => {
    if (!description) return '';
    
    // Remove the template part (everything before "Additional Information:")
    const parts = description.split('Additional Information:');
    if (parts.length > 1) {
      return 'Additional Information:' + parts[1];
    }
    
    // If no "Additional Information:" found, check for common template patterns
    const lines = description.split('\n');
    const cleanLines = [];
    let skipTemplate = false;
    
    for (const line of lines) {
      // Skip template lines with placeholders
      if (line.includes('[DATE]') || line.includes('[LOCATION]') || 
          line.includes('[NUMBER]') || line.includes('[REASON]') ||
          line.includes('I am requesting a copy') ||
          line.includes('Date of Incident: [') ||
          line.includes('Please provide a certified copy')) {
        skipTemplate = true;
        continue;
      }
      
      // Start collecting actual data after template
      if (line.includes('Date of Incident:') && !line.includes('[DATE]')) {
        skipTemplate = false;
      }
      
      if (!skipTemplate && line.trim()) {
        cleanLines.push(line);
      }
    }
    
    return cleanLines.join('\n') || description;
  };
  const selectRequest = (request) => {
    setSelectedRequest(request);
    loadMessages(request.id);
  };

  // Load data on component mount
  useEffect(() => {
    if (user && user.role === 'staff') {
      loadStaffData();
    }
  }, [user]);

  // Redirect if not staff
  if (!user || user.role !== 'staff') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600">Access denied. Staff access required.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-800">Staff Workspace</h1>
                <p className="text-sm text-slate-600">Request Management Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button 
                onClick={() => navigate('/dashboard')}
                variant="outline" 
                size="sm"
                className="text-slate-600 hover:text-blue-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              
              <div className="flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">{user?.full_name}</span>
              </div>
              
              <Button onClick={loadStaffData} variant="outline" size="sm" disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button 
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-red-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Stats and Request List */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.assigned}</div>
                  <div className="text-xs text-slate-600">Assigned</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
                  <div className="text-xs text-slate-600">Pending</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
                  <div className="text-xs text-slate-600">Completed</div>
                </CardContent>
              </Card>
            </div>

            {/* Request List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  My Assigned Requests
                </CardTitle>
                <CardDescription>
                  Click on a request to manage it
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myRequests.length > 0 ? (
                    myRequests.map((request) => (
                      <div
                        key={request.id}
                        onClick={() => selectRequest(request)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-blue-300 hover:shadow-md ${
                          selectedRequest?.id === request.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-slate-800 text-sm">{request.title}</h4>
                          <Badge 
                            variant={request.status === 'completed' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {request.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mb-2">
                          {cleanDescription(request.description).substring(0, 100)}...
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>{request.request_type.replace('_', ' ')}</span>
                          <span>•</span>
                          <span>{request.priority}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No assigned requests</p>
                      <p className="text-sm">Assigned requests will appear here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Request Details */}
          <div className="lg:col-span-2">
            {selectedRequest ? (
              <div className="space-y-6">
                
                {/* Request Header */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedRequest.title}</CardTitle>
                        <CardDescription className="mt-2">
                          {cleanDescription(selectedRequest.description)}
                        </CardDescription>
                      </div>
                      <Badge variant="outline">{selectedRequest.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-slate-600">Request Type</Label>
                        <p className="font-medium">{selectedRequest.request_type.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Priority</Label>
                        <p className="font-medium capitalize">{selectedRequest.priority}</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Submitted</Label>
                        <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Requester</Label>
                        <p className="font-medium">{selectedRequest.requester_name || 'Unknown'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Tabs defaultValue="status" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="status">Update Status</TabsTrigger>
                    <TabsTrigger value="communication">Communication</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                  </TabsList>
                  
                  {/* Status Update Tab */}
                  <TabsContent value="status">
                    <Card>
                      <CardHeader>
                        <CardTitle>Update Request Status</CardTitle>
                        <CardDescription>
                          Change the status of this request
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <Button
                            onClick={() => updateRequestStatus(selectedRequest.id, 'in_progress')}
                            variant={selectedRequest.status === 'in_progress' ? 'default' : 'outline'}
                            className="h-12"
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            In Progress
                          </Button>
                          <Button
                            onClick={() => updateRequestStatus(selectedRequest.id, 'completed')}
                            variant={selectedRequest.status === 'completed' ? 'default' : 'outline'}
                            className="h-12"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Completed
                          </Button>
                        </div>
                        <Button
                          onClick={() => updateRequestStatus(selectedRequest.id, 'denied')}
                          variant={selectedRequest.status === 'denied' ? 'destructive' : 'outline'}
                          className="w-full h-12"
                        >
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Denied
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Communication Tab */}
                  <TabsContent value="communication">
                    <Card>
                      <CardHeader>
                        <CardTitle>Communication</CardTitle>
                        <CardDescription>
                          Send messages to the requester
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Messages */}
                        <div className="max-h-64 overflow-y-auto space-y-3">
                          {messages.map((message) => (
                            <div
                              key={message.id}
                              className={`p-3 rounded-lg ${
                                message.sender_id === user?.id
                                  ? 'bg-blue-100 ml-8'
                                  : 'bg-slate-100 mr-8'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium">{message.sender_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {message.sender_role}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-700">{message.content}</p>
                            </div>
                          ))}
                          {messages.length === 0 && (
                            <div className="text-center py-4 text-slate-500">
                              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                              <p className="text-sm">No messages yet</p>
                            </div>
                          )}
                        </div>
                        
                        {/* New Message */}
                        <div className="flex gap-2">
                          <Textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            className="flex-1"
                            rows={2}
                          />
                          <Button onClick={sendMessage} disabled={!newMessage.trim()}>
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Documents Tab */}
                  <TabsContent value="documents">
                    <Card>
                      <CardHeader>
                        <CardTitle>Documents & Files</CardTitle>
                        <CardDescription>
                          Upload and manage files for this request
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                          <p className="text-slate-600 mb-2">Upload documents</p>
                          <p className="text-sm text-slate-500">Drag and drop files or click to browse</p>
                          <Input type="file" className="mt-4" multiple />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card className="h-96">
                <CardContent className="h-full flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-lg font-medium mb-2">Select a Request</h3>
                    <p>Choose a request from the left panel to start managing it</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffWorkspace;